import { css } from '@emotion/react';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverProps,
  Stack,
  Typography,
} from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { ConfirmDeletionDialog } from '../../../components/Dialog/ConfirmDialog/ConfirmDeletionDialog';
import { Loader } from '../../../components/Loader/Loader';
import { IAgentConversationDataModel } from '../../../schemas/agent-conversation.schema';
import { isMobileDevice } from '../../../util/mobile';
import { NoDataMessage2 } from '../../CompanyProfiles/components/Messages/HelpMessage';
import { MenuButton } from '../../Finance2/common-grid-defs/MenuButton';
import {
  ChatConversationIdAtom,
  ShowChatMenuAtom,
  useConversations,
  useCreateNewChat,
  useCurrentConversation,
  useDeleteConversation,
} from '../hooks/chatActionHooks';

const MobileOnlyDrawerContentStyles = css`
  width: min(100dvw, 350px);
  height: 100%;
  max-height: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
`;

const DesktopOnlyPopoverContentStyles = css`
  width: min(100dvw, 350px);
`;
const DrawerCloseButtonStyles = css`
  display: block;
  justify-self: end;
  padding-top: 1rem;
  padding-right: 1rem;
`;

const NewChatSectionStyles = css`
  width: 100%;
  display: grid;

  &&&&& .new-chat-button {
    justify-self: stretch;
    justify-content: start;
    margin-inline: 1rem;

    @media (min-width: 768px) {
      margin-top: 2rem;
    }
  }
`;

const ChatIconStyles = css`
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ConversationItemStyles = css`
  align-items: center;
  border-radius: 4px;
  justify-content: space-between;
  & svg {
    color: var(--mui-text-secondary);
  }
`;

const MenuItemsStyles = css`
  display: grid;
  justify-items: start;
  align-items: start;
  justify-content: stretch;
  grid-template-rows: auto 1fr;
`;
const HistoryListStyles = css`
  height: fit-content;
  max-height: 80dvh;
  width: 100%;
  overflow-y: scroll;
  overscroll-behavior: contain;
  padding-inline: 1rem;
  padding-bottom: 1rem;
`;

interface IChatMenuDrawerProps {
  open: boolean;
  onClose: () => void;
  anchor?: PopoverProps['anchorEl'];
}

export const ChatMenuDrawerAriaLabel = 'foresight-assistant-options';
function ChatMenuDrawer({ open, onClose }: IChatMenuDrawerProps) {
  return (
    <Drawer open={open} onClose={onClose} anchor='left' aria-label={ChatMenuDrawerAriaLabel}>
      <Box css={MobileOnlyDrawerContentStyles}>
        <IconButton onClick={onClose} css={DrawerCloseButtonStyles} title='close menu'>
          <CloseIcon css={ChatIconStyles} />
        </IconButton>

        <ChatMenuOptions />
      </Box>
    </Drawer>
  );
}

function ChatMenuPopover({ open, onClose, anchor }: IChatMenuDrawerProps) {
  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      anchorEl={anchor}
      anchorPosition={{ top: 0, left: 0 }}
      aria-label={ChatMenuDrawerAriaLabel}
    >
      <Box css={DesktopOnlyPopoverContentStyles}>
        <ChatMenuOptions />
      </Box>
    </Popover>
  );
}

/** The conversation menu (mobile: left drawer, desktop: popover). The trigger
 *  lives in ChatHeader, which drives `open` via ShowChatMenuAtom. */
export function ChatMenuContainer({ open, anchor }: IChatMenuDrawerProps) {
  const setShowChatMenu = useSetAtom(ShowChatMenuAtom);
  const isMobile = isMobileDevice(true);
  const onClick = useCallback(() => {
    setShowChatMenu(false);
  }, [setShowChatMenu]);

  if (isMobile) {
    return <ChatMenuDrawer open={open} onClose={onClick} />;
  } else {
    return <ChatMenuPopover open={open} onClose={onClick} anchor={anchor} />;
  }
}

function ChatMenuOptions() {
  const { data: conversations, isLoading, isError } = useConversations();
  const onCreateNew = useCreateNewChat();
  const conversationId = useAtomValue(ChatConversationIdAtom);

  const handleCreateNew = useCallback(() => {
    onCreateNew();
  }, [onCreateNew]);

  const chatHistorySection = useMemo(() => {
    if (isLoading) {
      return (
        <Stack alignContent='start' justifyContent='center' width='100%' pt='2rem'>
          <Loader />
        </Stack>
      );
    }
    if (isError) {
      return <Alert severity='error'>Failed to load history</Alert>;
    }
    if (conversations?.length === 0) {
      return <NoDataMessage2 message='You have no history yet' sx={{ height: 'unset' }} />;
    }
    return (
      <>
        <Typography variant='body2' pl={'0.5rem'} color='text.secondary'>
          History
        </Typography>
        <MenuList>
          {conversations?.map((conversation) => (
            <ChatMenuItem
              key={conversation.id}
              conversation={conversation}
              selected={conversation.id === conversationId}
            />
          ))}
        </MenuList>
      </>
    );
  }, [isLoading, isError, conversations, conversationId]);

  return (
    <Box css={MenuItemsStyles}>
      <Box css={NewChatSectionStyles}>
        <Button
          startIcon={<AddCircleIcon color='primary' fontSize='large' />}
          size='large'
          onClick={handleCreateNew}
          color='primary'
          className='new-chat-button'
        >
          New Chat
        </Button>
        <Divider sx={{ width: '100%', marginBlock: '0.5rem 1rem' }} />
      </Box>
      <Box css={HistoryListStyles}>{chatHistorySection}</Box>
    </Box>
  );
}

interface IChatMenuItemProps {
  conversation: IAgentConversationDataModel;
  selected: boolean;
}
function ChatMenuItem({ conversation, selected }: IChatMenuItemProps) {
  const { onSelectConversation } = useCurrentConversation();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const onClickDelete = useCallback((e?: SyntheticEvent) => {
    e?.stopPropagation();
    setShowConfirmDelete(true);
  }, []);

  const onSelect = useCallback(() => {
    onSelectConversation(conversation.id);
  }, [onSelectConversation, conversation.id]);

  const actionMenuItems = useMemo(() => {
    return [
      {
        name: 'Delete',
        icon: <DeleteOutlineOutlinedIcon />,
        onClick: onClickDelete,
      },
    ];
  }, [onClickDelete]);

  const title = conversation.title || conversation.timestamp || `No title (ID: ${conversation.id})`;

  return (
    <>
      {showConfirmDelete && (
        <ConfirmDeleteConversationDialog
          open={showConfirmDelete}
          onClose={() => setShowConfirmDelete(false)}
          conversation={conversation}
        />
      )}

      <MenuItem color='primary' key={conversation.id} css={ConversationItemStyles} selected={selected}>
        <Typography noWrap title={title} onClick={onSelect} width='100%'>
          {title}
        </Typography>
        <MenuButton menuItems={actionMenuItems} label='conversation actions' />
      </MenuItem>
    </>
  );
}

function ConfirmDeleteConversationDialog({
  open,
  onClose,
  conversation,
}: Pick<Parameters<typeof ConfirmDeletionDialog>[0], 'open' | 'onClose'> & {
  conversation: IAgentConversationDataModel;
}) {
  const [loading, setLoading] = useState(false);
  const deleteConversation = useDeleteConversation();

  const onDelete = useCallback(async () => {
    setLoading(true);
    await deleteConversation(conversation.id);
    setLoading(false);
    onClose();
  }, [deleteConversation, conversation.id, onClose]);

  return (
    <ConfirmDeletionDialog
      open={open}
      onClose={onClose}
      onConfirm={onDelete}
      title={'Delete Conversation'}
      loading={loading}
    >
      <Typography variant='body2' color='text.secondary'>
        {`Are you sure you want to permanently delete "${conversation.title}"?`}
      </Typography>
    </ConfirmDeletionDialog>
  );
}
